// Landing Page Content - English & Arabic
// Centralized content management for easy updates

export const landingContent = {
  en: {
    // Hero Section
    hero: {
      headline: "Your AI Career Partner",
      headlineHighlight: "Build, Improve, and Launch Your Future",
      subheadline: "Create a professional CV, explore job opportunities, and get personalized career guidance — all in one intelligent platform.",
      ctaPrimary: "Build My CV",
      ctaSecondary: "Explore Jobs",
      stats: {
        users: "10,000+",
        usersLabel: "Happy Users",
        rating: "4.9/5",
        ratingLabel: "User Rating"
      }
    },

    // About Section
    about: {
      title: "What is CVLogs?",
      description: "CVLogs is an AI-powered career platform that helps job seekers create exceptional CVs, discover tailored job matches, and communicate with intelligent assistants for professional growth.",
      mission: "Our mission is to make career building smarter, simpler, and more human-centered."
    },

    // CV Builder Section
    cvBuilder: {
      title: "AI-Powered CV Creation",
      description: "Build your professional CV in minutes using AI. Our system helps you write, structure, and enhance each section — from summary to experience — with smart suggestions and real-time editing.",
      features: [
        "Auto-generate CVs with your inputs",
        "Edit each section using natural language",
        "Save versions and track changes",
        "Download in multiple formats (PDF, DOCX)"
      ],
      cta: "Start Building"
    },

    // Job Discovery Section
    jobDiscovery: {
      title: "Find Jobs That Fit You",
      subtitle: "Instantly",
      description: "Explore job listings from multiple sources like LinkedIn, Indeed, and Google Jobs, all curated in one place. Our AI learns from your profile and recommends the most relevant opportunities.",
      features: [
        "Advanced filters by title, skills, and location",
        "Personalized job recommendations",
        "Real-time job tracking and saved searches",
        "AI-based job matching"
      ],
      cta: "Search Jobs"
    },

    // Cover Letters Section
    coverLetters: {
      title: "Stand Out with a Personalized Cover Letter",
      description: "Generate professional cover or motivation letters tailored to each job. Highlight your strengths and align with the company's goals — instantly.",
      features: [
        "AI writing assistant for each job",
        "Multi-language support",
        "Edit and approve before saving",
        "Export with your CV in one click"
      ],
      cta: "Create Cover Letter"
    },

    // AI Assistant Section
    aiAssistant: {
      title: "Your 24/7 Career Coach",
      description: "Chat with your personal AI agent to get instant help with writing, interview preparation, or career advice. The assistant learns from your profile to give accurate and practical guidance.",
      features: [
        "Career Q&A",
        "CV review & improvement tips",
        "Job recommendation explanations",
        "Live tracking of your job applications"
      ],
      cta: "Try the Assistant"
    },

    // Learning Section
    learning: {
      title: "Grow Your Skills. Expand Your Future.",
      description: "Access curated articles and courses to help you improve your career potential — from resume writing to interview mastery and technical upskilling.",
      features: [
        "Free & premium lessons",
        "Personalized learning recommendations",
        "Career tips blog",
        "Expert-authored content"
      ],
      cta: "Start Learning"
    },

    // For Employers Section
    employers: {
      title: "Hire Smarter with CVLogs",
      description: "CVLogs empowers companies to post jobs, manage applications, and find top talent through AI-driven candidate matching. Streamline your hiring process and connect with the right people faster.",
      features: [
        "Post and manage job listings",
        "AI candidate matching",
        "Application tracking dashboard",
        "Direct chat with applicants"
      ],
      ctaPrimary: "Post a Job",
      ctaSecondary: "Request a Demo"
    },

    // Pricing Section
    pricing: {
      title: "Flexible Plans for Every Professional",
      description: "Whether you're just starting or want to unlock advanced AI tools, CVLogs offers flexible monthly and annual plans tailored to your needs.",
      cta: "Get Started"
    },

    // Newsletter Section
    newsletter: {
      title: "Stay Ahead — Join Our Career Community",
      description: "Get exclusive tips, AI updates, and new job alerts directly to your inbox.",
      placeholder: "Enter your email",
      cta: "Join Now"
    },

    // CTA Section
    cta: {
      title: "Ready to Build Your Perfect CV?",
      titleUser: "Welcome Back, Professional!",
      description: "Join thousands of professionals who have successfully landed their dream jobs with CVLogs.",
      descriptionUser: "Continue working on your CV or start a new one. Your success is our priority.",
      ctaPrimary: "Get Started Free",
      ctaPrimaryUser: "Build New CV",
      ctaSecondary: "AI Assistant",
      badges: [
        "Secure & Private",
        "2 Min Setup",
        "No Credit Card"
      ]
    },

    // Footer
    footer: {
      about: "About",
      blog: "Blog",
      pricing: "Pricing",
      careers: "Careers",
      contact: "Contact",
      copyright: "© 2025 CVLogs. All rights reserved."
    },

    // Before/After Slider Section
    beforeAfter: {
      title: "See the Difference Instantly",
      subtitle: "Drag the slider to compare a traditional resume with a cvLogs ATS-friendly resume",
      beforeLabel: "Traditional Cluttered CV",
      afterLabel: "cvLogs ATS-Optimized CV",
      beforeTitle: "CURRICULUM VITAE",
      afterTitle: "AHMED ABDELRAHMAN",
      beforeSub: "My Information / Resume",
      afterSub: "Senior Software Engineer",
      beforeSummary: "I am a developer looking for a job. I have experience writing code, fixing bugs, and working in teams. I am very hard working and want to join a company.",
      afterSummary: "Results-driven Senior Software Engineer with 5+ years of experience specializing in high-performance React architectures. Proven track record of reducing latency by 40% and leading cross-functional engineering teams.",
      beforeExpTitle: "WORK HISTORY",
      afterExpTitle: "PROFESSIONAL EXPERIENCE",
      beforeExpBody: "Developer at Bytelogs\n- Was responsible for writing code and fixing bugs.\n- Attended team meetings and worked with other people.",
      afterExpBody: "Senior Frontend Engineer | Bytelogs Ltd\n- Spearheaded redesign of core React platform, improving performance and user engagement by 35%.\n- Mentored 4 junior developers and established automated testing protocols.",
      beforeScore: "ATS Score: 32%",
      afterScore: "ATS Match: 96%",
      beforeFeedback: "⚠️ Spelling errors, weak verbs, no metrics.",
      afterFeedback: "✨ High visibility, action verbs, quantified results."
    }
  },

  ar: {
    // Hero Section
    hero: {
      headline: "شريكك المهني بالذكاء الاصطناعي",
      headlineHighlight: "ابنِ، حسّن، وأطلق مستقبلك",
      subheadline: "أنشئ سيرة ذاتية احترافية، استكشف فرص العمل، واحصل على إرشادات مهنية مخصصة — كل ذلك في منصة ذكية واحدة.",
      ctaPrimary: "ابنِ سيرتي الذاتية",
      ctaSecondary: "استكشف الوظائف",
      stats: {
        users: "+10,000",
        usersLabel: "مستخدم سعيد",
        rating: "4.9/5",
        ratingLabel: "تقييم المستخدمين"
      }
    },

    // About Section
    about: {
      title: "ما هو CVLogs؟",
      description: "CVLogs هي منصة مهنية مدعومة بالذكاء الاصطناعي تساعد الباحثين عن عمل على إنشاء سير ذاتية استثنائية، واكتشاف فرص عمل مناسبة، والتواصل مع مساعدين أذكياء للنمو المهني.",
      mission: "مهمتنا هي جعل بناء المسيرة المهنية أذكى وأبسط وأكثر تركيزاً على الإنسان."
    },

    // CV Builder Section
    cvBuilder: {
      title: "إنشاء السيرة الذاتية بالذكاء الاصطناعي",
      description: "ابنِ سيرتك الذاتية الاحترافية في دقائق باستخدام الذكاء الاصطناعي. يساعدك نظامنا على الكتابة والهيكلة والتحسين لكل قسم — من الملخص إلى الخبرة — مع اقتراحات ذكية وتحرير فوري.",
      features: [
        "توليد تلقائي للسير الذاتية من بياناتك",
        "تحرير كل قسم باستخدام اللغة الطبيعية",
        "حفظ الإصدارات وتتبع التغييرات",
        "التنزيل بصيغ متعددة (PDF، DOCX)"
      ],
      cta: "ابدأ الإنشاء"
    },

    // Job Discovery Section
    jobDiscovery: {
      title: "ابحث عن وظائف تناسبك",
      subtitle: "فوراً",
      description: "استكشف قوائم الوظائف من مصادر متعددة مثل LinkedIn وIndeed وGoogle Jobs، كلها منسقة في مكان واحد. يتعلم الذكاء الاصطناعي من ملفك الشخصي ويوصي بالفرص الأكثر صلة.",
      features: [
        "فلاتر متقدمة حسب المسمى والمهارات والموقع",
        "توصيات وظيفية مخصصة",
        "تتبع الوظائف في الوقت الفعلي والبحث المحفوظ",
        "مطابقة الوظائف بالذكاء الاصطناعي"
      ],
      cta: "ابحث عن وظائف"
    },

    // Cover Letters Section
    coverLetters: {
      title: "تميّز برسالة تغطية مخصصة",
      description: "أنشئ رسائل تغطية أو تحفيزية احترافية مخصصة لكل وظيفة. سلّط الضوء على نقاط قوتك وتوافق مع أهداف الشركة — فوراً.",
      features: [
        "مساعد كتابة بالذكاء الاصطناعي لكل وظيفة",
        "دعم متعدد اللغات",
        "تحرير والموافقة قبل الحفظ",
        "التصدير مع سيرتك الذاتية بنقرة واحدة"
      ],
      cta: "أنشئ رسالة تغطية"
    },

    // AI Assistant Section
    aiAssistant: {
      title: "مدربك المهني على مدار الساعة",
      description: "تحدث مع وكيلك الذكي الشخصي للحصول على مساعدة فورية في الكتابة أو الإعداد للمقابلات أو النصائح المهنية. يتعلم المساعد من ملفك الشخصي لتقديم إرشادات دقيقة وعملية.",
      features: [
        "أسئلة وأجوبة مهنية",
        "مراجعة السيرة الذاتية ونصائح التحسين",
        "شرح توصيات الوظائف",
        "تتبع مباشر لطلبات التوظيف"
      ],
      cta: "جرّب المساعد"
    },

    // Learning Section
    learning: {
      title: "طوّر مهاراتك. وسّع مستقبلك.",
      description: "احصل على مقالات ودورات منسقة لمساعدتك على تحسين إمكاناتك المهنية — من كتابة السيرة الذاتية إلى إتقان المقابلات وتطوير المهارات التقنية.",
      features: [
        "دروس مجانية ومميزة",
        "توصيات تعليمية مخصصة",
        "مدونة نصائح مهنية",
        "محتوى من خبراء"
      ],
      cta: "ابدأ التعلم"
    },

    // For Employers Section
    employers: {
      title: "وظّف بذكاء مع CVLogs",
      description: "تمكّن CVLogs الشركات من نشر الوظائف وإدارة الطلبات والعثور على أفضل المواهب من خلال مطابقة المرشحين بالذكاء الاصطناعي. بسّط عملية التوظيف وتواصل مع الأشخاص المناسبين بشكل أسرع.",
      features: [
        "نشر وإدارة قوائم الوظائف",
        "مطابقة المرشحين بالذكاء الاصطناعي",
        "لوحة تتبع الطلبات",
        "محادثة مباشرة مع المتقدمين"
      ],
      ctaPrimary: "انشر وظيفة",
      ctaSecondary: "اطلب عرضاً توضيحياً"
    },

    // Pricing Section
    pricing: {
      title: "خطط مرنة لكل محترف",
      description: "سواء كنت بدأت للتو أو تريد فتح أدوات الذكاء الاصطناعي المتقدمة، تقدم CVLogs خططاً شهرية وسنوية مرنة مصممة لاحتياجاتك.",
      cta: "ابدأ الآن"
    },

    // Newsletter Section
    newsletter: {
      title: "كن في المقدمة — انضم إلى مجتمعنا المهني",
      description: "احصل على نصائح حصرية وتحديثات الذكاء الاصطناعي وتنبيهات الوظائف الجديدة مباشرة إلى بريدك الإلكتروني.",
      placeholder: "أدخل بريدك الإلكتروني",
      cta: "انضم الآن"
    },

    // CTA Section
    cta: {
      title: "هل أنت مستعد لبناء سيرتك الذاتية المثالية؟",
      titleUser: "مرحباً بعودتك، محترف!",
      description: "انضم إلى آلاف المحترفين الذين نجحوا في الحصول على وظائف أحلامهم مع CVLogs.",
      descriptionUser: "تابع العمل على سيرتك الذاتية أو ابدأ واحدة جديدة. نجاحك هو أولويتنا.",
      ctaPrimary: "ابدأ مجاناً",
      ctaPrimaryUser: "أنشئ سيرة ذاتية جديدة",
      ctaSecondary: "المساعد الذكي",
      badges: [
        "آمن وخاص",
        "إعداد في دقيقتين",
        "لا حاجة لبطاقة ائتمان"
      ]
    },

    // Footer
    footer: {
      about: "حول",
      blog: "المدونة",
      pricing: "التسعير",
      careers: "الوظائف",
      contact: "اتصل بنا",
      copyright: "© 2025 CVLogs. جميع الحقوق محفوظة."
    },

    // Before/After Slider Section
    beforeAfter: {
      title: "شاهد الفرق بنفسك فوراً",
      subtitle: "اسحب الشريط المقارن لترى الفرق بين السيرة الذاتية التقليدية وسيرة cvLogs المتوافقة مع الـ ATS",
      beforeLabel: "سيرة ذاتية تقليدية وغير منظمة",
      afterLabel: "سيرة cvLogs الذكية والمتوافقة",
      beforeTitle: "سيرة ذاتية (C.V)",
      afterTitle: "أحمد عبد الرحمن",
      beforeSub: "المعلومات الشخصية والخبرة",
      afterSub: "مهندس برمجيات أول",
      beforeSummary: "أنا مبرمج أبحث عن وظيفة مناسبة. لدي خبرة في كتابة الأكواد وحل المشكلات والعمل الجماعي. أنا مجتهد جداً وأتطلب للانضمام لشركتكم الموقرة.",
      afterSummary: "مهندس برمجيات أول بخبرة تزيد عن 5 سنوات في تصميم وبناء تطبيقات الويب فائقة الأداء باستخدام React. شغوف بتبسيط البنية البرمجية وتحسين سرعة الأنظمة بنسبة 40% وقيادة فرق التطوير.",
      beforeExpTitle: "تاريخ العمل",
      afterExpTitle: "الخبرة المهنية",
      beforeExpBody: "مبرمج في شركة Bytelogs\n- كنت مسؤولاً عن كتابة الأكواد وتصليح الأخطاء.\n- حضرت الاجتماعات وعملت مع الزملاء بالقسم.",
      afterExpBody: "مهندس واجهات أمامية أول | Bytelogs Ltd\n- قدت عملية إعادة تصميم الواجهة الأساسية لمنصة React، مما أدى لتحسين الأداء بنسبة 35% وزيادة تفاعل المستخدمين.\n- أشرفت على توجيه 4 مطورين مبتدئين وتأسيس بروتوكولات اختبار آلية.",
      beforeScore: "تقييم الـ ATS: %32",
      afterScore: "تطابق الـ ATS: %96",
      beforeFeedback: "⚠️ أخطاء إملائية، عبارات عامة، غياب أرقام الإنجاز.",
      afterFeedback: "✨ توافق عالٍ، أفعال قوية، إنجازات مرقمنة ومقاسة."
    }
  },
  tr: {
  "hero": {
    "headline": "Yapay Zeka Kariyer Ortağınız",
    "headlineHighlight": "Geleceğinizi İnşa Edin, Geliştirin ve Başlatın",
    "subheadline": "Profesyonel bir CV oluşturun, iş fırsatlarını keşfedin ve kişiselleştirilmiş kariyer rehberliği alın; hepsi tek bir akıllı platformda.",
    "ctaPrimary": "CV'mi Oluştur",
    "ctaSecondary": "İşleri Keşfedin",
    "stats": {
      "users": "10.000+",
      "usersLabel": "Mutlu Kullanıcılar",
      "rating": "4,9/5",
      "ratingLabel": "Kullanıcı Değerlendirmesi"
    }
  },
  "about": {
    "title": "CVLogs nedir?",
    "description": "CVLogs, iş arayanların olağanüstü CV'ler oluşturmasına, özel iş eşleşmelerini keşfetmesine ve profesyonel gelişim için akıllı asistanlarla iletişim kurmasına yardımcı olan, yapay zeka destekli bir kariyer platformudur.",
    "mission": "Misyonumuz kariyer oluşturmayı daha akıllı, daha basit ve daha insan merkezli hale getirmektir."
  },
  "cvBuilder": {
    "title": "Yapay Zeka Destekli CV Oluşturma",
    "description": "Yapay zekayı kullanarak birkaç dakika içinde profesyonel CV'nizi oluşturun. Sistemimiz, akıllı öneriler ve gerçek zamanlı düzenlemeyle özetten deneyime kadar her bölümü yazmanıza, yapılandırmanıza ve geliştirmenize yardımcı olur.",
    "features": [
      "Girişlerinizle CV'leri otomatik olarak oluşturun",
      "Her bölümü doğal dili kullanarak düzenleyin",
      "Sürümleri kaydedin ve değişiklikleri izleyin",
      "Birden fazla formatta indirin (PDF, DOCX)"
    ],
    "cta": "İnşa Etmeye Başla"
  },
  "jobDiscovery": {
    "title": "Size Uygun İşleri Bulun",
    "subtitle": "Aniden",
    "description": "Hepsi tek bir yerde derlenen LinkedIn, Indeed ve Google Jobs gibi birden fazla kaynaktaki iş ilanlarını keşfedin. Yapay zekamız profilinizden öğrenir ve en alakalı fırsatları önerir.",
    "features": [
      "Başlığa, becerilere ve konuma göre gelişmiş filtreler",
      "Kişiselleştirilmiş iş önerileri",
      "Gerçek zamanlı iş takibi ve kaydedilen aramalar",
      "Yapay zeka tabanlı iş eşleştirme"
    ],
    "cta": "İş Ara"
  },
  "coverLetters": {
    "title": "Kişiselleştirilmiş Bir Kapak Mektubu ile Öne Çıkın",
    "description": "Her işe özel profesyonel kapak veya motivasyon mektupları oluşturun. Güçlü yönlerinizi vurgulayın ve şirketin hedeflerine anında uyum sağlayın.",
    "features": [
      "Her iş için yapay zeka yazma asistanı",
      "Çoklu dil desteği",
      "Kaydetmeden önce düzenleyin ve onaylayın",
      "Tek tıklamayla CV'nizle birlikte dışa aktarın"
    ],
    "cta": "Kapak Mektubu Oluştur"
  },
  "aiAssistant": {
    "title": "7/24 Kariyer Koçunuz",
    "description": "Yazma, röportaj hazırlığı veya kariyer tavsiyesi konularında anında yardım almak için kişisel AI temsilcinizle sohbet edin. Asistan, doğru ve pratik rehberlik sağlamak için profilinizden bilgi alır.",
    "features": [
      "Kariyer Soru-Cevap",
      "CV inceleme ve iyileştirme ipuçları",
      "İş önerisi açıklamaları",
      "İş başvurularınızın canlı takibi"
    ],
    "cta": "Asistanı deneyin"
  },
  "learning": {
    "title": "Becerilerinizi Geliştirin. Geleceğinizi Genişletin.",
    "description": "Özgeçmiş yazmaktan röportaj ustalığına ve teknik beceri geliştirmeye kadar kariyer potansiyelinizi geliştirmenize yardımcı olacak seçilmiş makalelere ve kurslara erişin.",
    "features": [
      "Ücretsiz ve premium dersler",
      "Kişiselleştirilmiş öğrenme önerileri",
      "Kariyer ipuçları blogu",
      "Uzman tarafından yazılan içerik"
    ],
    "cta": "Öğrenmeye Başla"
  },
  "employers": {
    "title": "CVLogs ile Daha Akıllı İşe Alın",
    "description": "CVLogs, şirketlerin iş ilanlarını yayınlamasına, uygulamaları yönetmesine ve yapay zeka odaklı aday eşleştirme yoluyla en iyi yetenekleri bulmasına olanak tanır. İşe alma sürecinizi kolaylaştırın ve doğru kişilerle daha hızlı bağlantı kurun.",
    "features": [
      "İş ilanlarını yayınlayın ve yönetin",
      "Yapay zeka aday eşleştirmesi",
      "Uygulama izleme panosu",
      "Başvuranlarla doğrudan sohbet"
    ],
    "ctaPrimary": "İş ilanı ver",
    "ctaSecondary": "Demo Talep Edin"
  },
  "pricing": {
    "title": "Her Profesyonele Uygun Esnek Planlar",
    "description": "İster yeni başlıyor olun ister gelişmiş yapay zeka araçlarının kilidini açmak isteyin, CVLogs ihtiyaçlarınıza göre uyarlanmış esnek aylık ve yıllık planlar sunar.",
    "cta": "Başlayın"
  },
  "newsletter": {
    "title": "Önde Kalın — Kariyer Topluluğumuza Katılın",
    "description": "Özel ipuçlarını, yapay zeka güncellemelerini ve yeni iş uyarılarını doğrudan gelen kutunuza alın.",
    "placeholder": "E-postanızı girin",
    "cta": "Şimdi Katılın"
  },
  "cta": {
    "title": "Mükemmel CV'nizi Oluşturmaya Hazır mısınız?",
    "titleUser": "Tekrar hoş geldiniz, Profesyonel!",
    "description": "CVLogs ile hayallerindeki işe başarıyla ulaşan binlerce profesyonele katılın.",
    "descriptionUser": "CV'niz üzerinde çalışmaya devam edin veya yeni bir tane başlatın. Başarınız bizim önceliğimizdir.",
    "ctaPrimary": "Ücretsiz Başlayın",
    "ctaPrimaryUser": "Yeni CV Oluştur",
    "ctaSecondary": "Yapay Zeka Asistanı",
    "badges": [
      "Güvenli ve Özel",
      "2 Dakika Kurulum",
      "Kredi Kartı Yok"
    ]
  },
  "footer": {
    "about": "Hakkında",
    "blog": "Blog",
    "pricing": "Fiyatlandırma",
    "careers": "Kariyer",
    "contact": "Temas etmek",
    "copyright": "© 2025 CVLogs. Her hakkı saklıdır."
  },
  "beforeAfter": {
    "title": "Farkı Anında Görün",
    "subtitle": "Geleneksel bir özgeçmişi cvLogs ATS dostu bir özgeçmişle karşılaştırmak için kaydırıcıyı sürükleyin",
    "beforeLabel": "Geleneksel Dağınık CV",
    "afterLabel": "cvLogs ATS için Optimize Edilmiş CV",
    "beforeTitle": "HAYAT MÜFREDATI",
    "afterTitle": "AHMED ABDELRAHMAN",
    "beforeSub": "Bilgilerim / Özgeçmişim",
    "afterSub": "Kıdemli Yazılım Mühendisi",
    "beforeSummary": "İş arayan bir geliştiriciyim. Kod yazma, hataları düzeltme ve ekip halinde çalışma deneyimim var. Çok çalışıyorum ve bir şirkete katılmak istiyorum.",
    "afterSummary": "Yüksek performanslı React mimarilerinde uzmanlaşmış, 5 yıldan fazla deneyime sahip, sonuç odaklı Kıdemli Yazılım Mühendisi. Gecikmeyi %40 oranında azaltma ve işlevler arası mühendislik ekiplerine liderlik etme konusunda kanıtlanmış performans geçmişi.",
    "beforeExpTitle": "ÇALIŞMA GEÇMİŞİ",
    "afterExpTitle": "MESLEKİ DENEYİM",
    "beforeExpBody": "Bytelogs'ta geliştirici \n- Kod yazma ve hataların düzeltilmesinden sorumluydum. \n- Ekip toplantılarına katıldım ve diğer insanlarla çalıştım.",
    "afterExpBody": "Kıdemli Ön Uç Mühendisi | Bytelogs Ltd \n- Temel React platformunun çığır açan yeniden tasarımı, performansı ve kullanıcı etkileşimini %35 artırdı. \n- 4 genç geliştiriciye danışmanlık yaptı ve otomatik test protokolleri oluşturdu.",
    "beforeScore": "ATS Puanı: %32",
    "afterScore": "ATS Maçı: %96",
    "beforeFeedback": "⚠️Yazım hataları, zayıf fiiller, ölçü yok.",
    "afterFeedback": "✨ Yüksek görünürlük, eylem fiilleri, ölçülmüş sonuçlar."
  }
}
};

// Helper function to get content by language
export const getContent = (language = 'en') => {
  return landingContent[language] || landingContent.en;
};
